import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import { loadUserSettings, saveFocusedEntry } from "../utils/entry-flow";
import { getKetoneActionLevel } from "../config/dafne/ketone-alerts";
import { DEFAULT_SETTINGS } from "../types";
import type { UserSettings } from "../types";

/**
 * Focused flow: log a ketone reading with DAFNE action-level guidance. No GL
 * required. See docs/add-task-flows.md.
 */
@customElement("add-ketone-page")
export class AddKetonePage extends LitElement {
  @state() private ketones = "";
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };

  static styles = [flowStyles];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const ketones = parseFloat(this.ketones);
    if (isNaN(ketones) || ketones < 0 || ketones > 10) return;

    this.isSaving = true;
    try {
      await saveFocusedEntry({ ketones }, this.settings);
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save ketone reading:", err);
      this.isSaving = false;
    }
  }

  private _renderGuidance() {
    const ketones = parseFloat(this.ketones);
    if (isNaN(ketones) || ketones < 0) return nothing;

    const level = getKetoneActionLevel(ketones);
    const variantMap: Record<string, string> = {
      normal: "success",
      elevated: "warning",
      high: "danger",
      danger: "danger",
    };
    const iconMap: Record<string, string> = {
      normal: "circle-check",
      elevated: "triangle-exclamation",
      high: "triangle-exclamation",
      danger: "hexagon-exclamation",
    };

    return html`
      <div class="warning-section">
        <wa-callout variant=${variantMap[level.level]} open>
          <wa-icon name=${iconMap[level.level]} slot="icon"></wa-icon>
          <strong>${ketones.toFixed(1)} mmol/L - ${level.action}</strong>
          ${level.details.length > 0
            ? html`
                <ul style="margin: 0.5rem 0 0 0; padding-left: 1.25rem;">
                  ${level.details.map((d) => html`<li>${d}</li>`)}
                </ul>
              `
            : nothing}
          ${level.recheckMinutes
            ? html`<p style="margin-top: 0.5rem; font-size: 0.8rem;">
                Recheck ketones in ${Math.round(level.recheckMinutes / 60)}
                hours
              </p>`
            : nothing}
          ${level.seekHelp
            ? html`<p
                style="margin-top: 0.5rem; font-weight: 600; color: #dc3545;"
              >
                Contact your diabetes team or seek medical help
              </p>`
            : nothing}
        </wa-callout>
      </div>
    `;
  }

  render() {
    const ketones = parseFloat(this.ketones);
    const canSave =
      this.ketones !== "" && !isNaN(ketones) && ketones >= 0 && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Ketone reading saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Ketone</h1>
        </div>

        <div class="form-section">
          <div class="input-group">
            <div class="input-label">
              <wa-icon name="vial"></wa-icon>
              Ketone reading
            </div>
            <wa-input
              class="flow-input"
              type="number"
              inputmode="decimal"
              min="0"
              max="10"
              step="0.1"
              placeholder="e.g. 0.5"
              .value=${this.ketones}
              @input=${(e: Event) =>
                (this.ketones = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter" && canSave) this._handleSave();
              }}
              autofocus
            ></wa-input>
            <div class="unit-hint">mmol/L</div>
          </div>

          ${this._renderGuidance()}

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save ketone reading"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-ketone-page": AddKetonePage;
  }
}
